import type { PixabayImage, PixabaySearchInput } from '../pixabay';
import { useCallback, useEffect, useState } from 'react';
import css from './pixabay-search-panel.module.scss';

export type PixabaySearchPanelProps = {
  // Consumer-supplied search function — typically a thin wrapper
  // around `trpc.uploadthing.pixabaySearch.useMutation().mutateAsync`
  // (or .query()). Keeps the package free of tRPC client coupling.
  onSearch: (input: PixabaySearchInput) => Promise<PixabayImage[]>;
  // Fires when the user clicks "Use selected". The consumer is
  // expected to upload the URL via UTApi.uploadFilesFromUrl and append
  // the resulting attachment.
  onSelect: (images: PixabayImage[]) => void;
  initialQuery?: string;
  debounceMs?: number;
  multiSelect?: boolean;
  // Disables the search input/select button — e.g. while the consumer
  // resolves the upload-from-url promise.
  disabled?: boolean;
};

export const PixabaySearchPanel = ({
  onSearch,
  onSelect,
  initialQuery = '',
  debounceMs = 350,
  multiSelect = false,
  disabled,
}: PixabaySearchPanelProps) => {
  const [query, setQuery] = useState(initialQuery);
  const [results, setResults] = useState<PixabayImage[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const performSearch = useCallback(
    async (next: string) => {
      const trimmed = next.trim();
      if (trimmed.length === 0) {
        setResults([]);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const hits = await onSearch({
          query: trimmed,
          page: 1,
          perPage: 20,
          imageType: 'photo',
          orientation: 'all',
        });
        setResults(hits);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Search failed');
      } finally {
        setLoading(false);
      }
    },
    [onSearch]
  );

  useEffect(() => {
    const handle = setTimeout(() => {
      void performSearch(query);
    }, debounceMs);
    return () => clearTimeout(handle);
  }, [query, debounceMs, performSearch]);

  const toggle = (id: number) => {
    setSelectedIds((current) => {
      const next = new Set(multiSelect ? current : []);
      if (current.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleSelect = () => {
    const picked = results.filter((image) => selectedIds.has(image.id));
    if (picked.length === 0) return;
    onSelect(picked);
    setSelectedIds(new Set());
  };

  return (
    <div className={css.root}>
      <div className={css.searchRow}>
        <input
          className={css.searchInput}
          disabled={disabled}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search Pixabay…"
          type="search"
          value={query}
        />
      </div>
      {error && <div className={css.empty}>{error}</div>}
      {!error && results.length === 0 && !loading && (
        <div className={css.empty}>
          {query ? 'No matches.' : 'Type to search free Pixabay images.'}
        </div>
      )}
      {!error && results.length > 0 && (
        <div className={css.grid}>
          {results.map((image) => {
            const selected = selectedIds.has(image.id);
            return (
              <button
                className={`${css.tile} ${selected ? css.tileIsSelected : ''}`}
                key={image.id}
                onClick={() => toggle(image.id)}
                type="button"
              >
                <img alt={image.tags} src={image.previewURL} />
              </button>
            );
          })}
        </div>
      )}
      <div className={css.footer}>
        <span className={css.caption}>
          Images served by Pixabay (free for commercial use).
        </span>
        <button
          className={css.btn}
          disabled={selectedIds.size === 0 || disabled}
          onClick={handleSelect}
          type="button"
        >
          Use selected
        </button>
      </div>
    </div>
  );
};
